/**
 * @file CreditBillPicker.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 *
 * The bills a supplier credit relates to — Add Supplier Credit's "Relates
 * to" and the credit window's "What … relates to" — picked from the
 * supplier's (and its supplier group's) bills, SEARCHED ON THE SERVER as the
 * person types (Cam, 2026-10-14: "it would not allow me to choose the right
 * bill … bills came up in the search just not the one I wanted"). Both
 * screens used to load the newest 100 once and filter those in the browser,
 * so for Mole Valley (13,000 bills) or NWF (11,000) anything older than about
 * a fortnight could never be found.
 *
 * GET /api/bills/credit-ref-candidates?account_id&xero_contact_id&search —
 * the system's one search rule (number, amount, date, PO, supplier), newest
 * first, ~300ms after typing stops, only the newest answer kept
 * (useLatestRequest). Each option: date · supplier invoice no. · total · what
 * it owes · status. The bills already chosen are always in the list, tagged
 * ADDED (choosing one again does nothing), so searching for one never looks
 * like "not found". Extra options (Add Supplier Credit's "Choose POs…") lead
 * the list and go to `onExtra`.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import SearchableDropdown from './SearchableDropdown';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { BILL_SEARCH_FIELDS, billOptionLabel, moreToNarrowText } from '../utils/supplierCredits';

const SEARCH_DEBOUNCE_MS = 300;
const billValue = (id) => `bill:${id}`;

/**
 * @param {object} props
 * @param {number|null} props.accountId — the credit's supplier
 * @param {string|null} [props.xeroContactId]
 * @param {Array<object>} [props.selected] — the bills already chosen
 * @param {(bill: object) => void} props.onAdd
 * @param {Array<object>} [props.extraOptions] — SearchableDropdown options shown first
 * @param {(value: string) => void} [props.onExtra]
 * @param {boolean} [props.disabled]
 * @param {string} [props.placeholder]
 */
function CreditBillPicker({ accountId, xeroContactId = null, selected = [], onAdd, extraOptions = [], onExtra, disabled = false,
  placeholder = 'Add a related bill…' }) {
  const [term, setTerm] = useState('');
  const [result, setResult] = useState({ bills: [], matched: 0, included: [], error: null });
  const [loading, setLoading] = useState(false);
  const latest = useLatestRequest();
  const hasSupplier = !!(accountId || xeroContactId);
  // The chosen bills come back with the page (`included`), so each is listed
  // with its date, total and status even when handed over with less.
  const selectedKey = (selected || []).map(b => Number(b?.id)).filter(Boolean).sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (!hasSupplier) {
      latest.begin();
      setResult({ bills: [], matched: 0, included: [], error: null });
      setLoading(false);
      return undefined;
    }
    const ticket = latest.begin();
    const q = term.trim();
    const run = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (accountId) qs.set('account_id', String(accountId));
        if (xeroContactId) qs.set('xero_contact_id', xeroContactId);
        if (q) qs.set('search', q);
        if (selectedKey) qs.set('include_ids', selectedKey);
        const res = await fetch(`${API_URL}/api/bills/credit-ref-candidates?${qs}`, { credentials: 'include' });
        const d = await res.json();
        if (!latest.isLatest(ticket)) return;
        setResult(d.success
          ? { bills: d.bills || [], matched: d.matched ?? (d.bills || []).length, included: d.included || [], error: null }
          : { bills: [], matched: 0, included: [], error: d.error || 'The supplier\'s bills could not be read.' });
      } catch (err) {
        if (latest.isLatest(ticket)) setResult({ bills: [], matched: 0, included: [], error: `The supplier's bills could not be read: ${err.message}` });
      } finally {
        if (latest.isLatest(ticket)) setLoading(false);
      }
    };
    // The first page at once; a search once typing stops.
    if (!q) { run(); return undefined; }
    const timer = setTimeout(run, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [hasSupplier, accountId, xeroContactId, term, selectedKey, latest]);

  const chosen = useMemo(() => new Set((selected || []).map(b => Number(b.id))), [selected]);
  const options = useMemo(() => [
    ...extraOptions,
    ...(selected || []).filter(b => b && b.id).map((b) => {
      const full = [...result.included, ...result.bills].find(x => Number(x.id) === Number(b.id));
      return { value: billValue(b.id), label: full ? billOptionLabel(full, { supplierId: accountId })
        : (b.supplier_reference || b.bill_number || `Bill #${b.id}`), tag: 'ADDED', pinned: true };
    }),
    ...result.bills.filter(b => !chosen.has(Number(b.id))).map(b => ({
      value: billValue(b.id), label: billOptionLabel(b, { supplierId: accountId })
    }))
  ], [extraOptions, selected, result.bills, result.included, chosen, accountId]);

  // The bill objects behind the options, for onAdd.
  const byValue = useRef(new Map());
  byValue.current = new Map(result.bills.map(b => [billValue(b.id), b]));

  const note = result.error || (loading && result.bills.length ? 'Searching…' : moreToNarrowText(result.matched, result.bills.length));

  return (
    <SearchableDropdown
      value=""
      onChange={(value) => {
        if (extraOptions.some(o => o.value === value)) { if (onExtra) onExtra(value); return; }
        const bill = byValue.current.get(value);
        if (bill && !chosen.has(Number(bill.id))) onAdd(bill);
      }}
      options={options}
      disabled={disabled || !hasSupplier}
      placeholder={hasSupplier ? placeholder : 'Choose the supplier first'}
      searchPlaceholder="Search the supplier's bills — number, amount, date, PO…"
      onSearchChange={setTerm}
      serverFiltered
      loading={loading}
      listNote={note}
      searchHelp={<SearchHelp fields={BILL_SEARCH_FIELDS} amounts />}
    />
  );
}

export default CreditBillPicker;
